import React, { useState, useEffect, useMemo, useContext, useRef }  from 'react';
import { useParams, useHistory } from 'react-router-dom';
import AppContext from 'Utils/AppContext';
import { filterTablesForDisplay, filterForDisplay } from 'Utils/entityLevels';
import { Row, Col, Button, Card } from 'react-bootstrap';
import { getPieVisData, getEntityToColumnIncidenceMap, commafy } from 'Utils';
import OverviewDonut from './OverviewDonut';
import OverviewTable from './OverviewTable';
import LevelCrumbs from '../LevelCrumbs';
import EntityMarkSeries from '../EntityPanel/EntityMarkSeries';
import FolderResultsPanel from './FolderResultsPanel';
import IndexResultsPanel from './IndexResultsPanel';
import FetchContainer from 'Utils/FetchContainer';
import UnprocessedItemsPanel from '../../ScanMeta/UnprocessedItemsPanel';
import SkippedArchiveFilesDialog from '../../ScanMeta/SkippedArchiveFilesDialog';
import { unprocessedExtensionsToItems, excludedExtensionsToItems }
  from '../../ScanMeta/scanMetaUtils';
import {
  getOverview,
  clearDiscoverResultsCache,
  buildOverviewForPieVis,
  buildOverviewFromFolderIndex,
  buildOverviewFromIndex,
  buildEntityTablesFromOverview,
  buildTableSampledRowCountsMapForEntityTables,
  buildTableColumnEntityMapFromOverview,
} from 'DataDiscovery/discoverApi';

// Legacy synthetic keys the worker used to stuff into unprocessed_extension_counts to
// mark archive-skip reasons. Newer worker builds count by real extension and surface
// the archive count separately on scan_meta.archive_skipped_count, but we still need
// this set as a fallback signal for runs that completed before that change.
const LEGACY_ARCHIVE_SKIP_KEYS = new Set([
  '(archive-too-large)',
  '(archive-encrypted)',
  '(archive-too-many-entries)',
  '(archive-too-deep)',
  '(archive-bad-ratio)',
  '(archive-read-failed)',
]);

// Friendly label for a FILE_* datasourceType, used to title results views. The editor no longer
// lets users pick a type (the extension is the type; new sources are FILE_GENERIC_FOLDER), but
// older typed runs still carry these datasourceType values, so keep labels for all of them.
export const FILE_TYPE_LABELS = {
  FILE_DELIMITED_FOLDER: 'Delimited (CSV, TSV, pipe, etc.)',
  FILE_PARQUET_FOLDER: 'Parquet',
  FILE_GENERIC_FOLDER: 'Folder Search (mixed content)',
  FILE_DOCUMENT_FOLDER: 'Documents (PDF, DOCX)',
  FILE_JSON_FOLDER: 'JSON',
  FILE_XML_FOLDER: 'XML',
  FILE_EXCEL_FOLDER: 'Excel',
  FILE_FIXED_WIDTH_FOLDER: 'Fixed width',
  FILE_HL7_FOLDER: 'HL7 v2 messages',
  FILE_EDI_FOLDER: 'EDI (X12, EDIFACT)',
};

const OverviewPanel = (props) => {
  const params = useParams();
  const history = useHistory();
  const { entityLevels } = useContext(AppContext);
  const searchid = params.search;

  let schemaSearchid;
  if (searchid && String(searchid).includes('_')) {
    const parts = String(searchid).split('_');
    const timestamp = parts.pop();
    const prefix = parts.join('_');
    schemaSearchid = `${prefix}_${props.dbname}_${timestamp}`;
  } else {
    schemaSearchid = [props.dbname, searchid].filter(Boolean).join('_') || String(searchid);
  }
  const searchName = params.searchName;
  const [overview,setOverview] = useState({});
  const [data, setData] = useState([[], []]);
  const [colors, setColors] = useState([]);
  const [overviewTotal, setOverviewTotal] = useState(0);
  const [donutLevel, setDonutLevel] = useState(0);
  const [otherTotal, setOtherTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityTables, setEntityTables] = useState({});
  const [tablesToSampledRows, setTablesToSampledRows] = useState({});
  const [entityToColumnIncidenceMap, setEntityToColumnIncidenceMap] = useState({});
  const [overviewTables, setOverviewTables] = useState([]);
  const [scanMeta, setScanMeta] = useState(null);
  const [folderIndex, setFolderIndex] = useState(null);
  const [tableIndex, setTableIndex] = useState(null);
  const [from, setFrom] = useState(0);
  const [skippedDialogOpen, setSkippedDialogOpen] = useState(false);
  const entity = params.entity ? params.entity : 'ALL' ;
  const setError = props.setError;
  const inProgress = props.inProgress;
  const encryptedTables = props.encryptedTables;
  const isEncrypted = props.isEncrypted;
  const sampleRules = props.sampleRules;
  // Resolve the data source for this run. Prefer the numeric id from the orchestration
  // job (always populated, immutable) over the name (can be empty for sources created
  // through legacy wizard paths that didn't pass a name through).
  const connectionDetails = useMemo(() => {
    const list = props.connections || [];
    if (props.connectionId != null) {
      const byId = list.find((c) => c?.id === props.connectionId);
      if (byId) return byId;
    }
    if (props.connection && typeof props.connection === 'object') {
      return list.find((c) => c?.id === props.connection.id) || null;
    }
    if (typeof props.connection === 'string' && props.connection) {
      return list.find((c) => c?.name === props.connection) || null;
    }
    return null;
  }, [props.connections, props.connection, props.connectionId]);

  // Folder-search runs are the only ones the worker populates scan_meta on, so a
  // present scan_meta is a reliable second signal when the connection lookup misses
  // (e.g. AppContext connections not yet loaded).
  const hasScanMeta = !!scanMeta && (
    (scanMeta.candidate_sources && scanMeta.candidate_sources.length > 0)
    || Object.keys(scanMeta.unprocessed_extension_counts || {}).length > 0
    || scanMeta.scanned_at != null
  );
  const isFolderSearch =
    connectionDetails?.datasourceType === 'FILE_GENERIC_FOLDER' || hasScanMeta
    || folderIndex != null;

  const fetchDataHandler = async (searchid) => {
    try {
      const response = await getOverview(searchid);
      setScanMeta(response?.scan_meta ?? null);

      // Table-index (PeopleSoft-80K) runs store a small summary INDEX instead of per-
      // table rows: donut/summary come straight from the index's entity_totals
      // aggregate, and the scatter is fed by IndexResultsPanel's own single page fetch
      // (results/tables?sort=findings&size=100) rather than anything loaded here.
      if (response?.tableIndex) {
        setTableIndex(response.tableIndex);
        setFolderIndex(null);
        const overviewData = filterForDisplay(buildOverviewFromIndex(response.tableIndex), entityLevels);
        const { otherCount, newData, total, newColors } = getPieVisData(overviewData);
        setOverview(overviewData);
        setOtherTotal(otherCount);
        setData(newData);
        setOverviewTotal(total);
        setColors(newColors);
        setEntityTables({});
        setTablesToSampledRows({});
        setEntityToColumnIncidenceMap({});
        setOverviewTables([]);
        return;
      }
      setTableIndex(null);

      // Folder Search runs store the folder INDEX instead of per-file rows: donut and
      // entity list are built from the index's per-folder entity rollups (sampled-
      // instance counts — the only aggregate the index carries), and per-file rows are
      // lazy-fetched per folder by FolderResultsPanel. HIDDEN entities are dropped
      // from the rollup totals, mirroring filterTablesForDisplay on the classic path.
      if (response?.folders) {
        setFolderIndex(response.folders);
        const overviewData = filterForDisplay(buildOverviewFromFolderIndex(response.folders), entityLevels);
        const { otherCount, newData, total, newColors } = getPieVisData(overviewData);
        setOverview(overviewData);
        setOtherTotal(otherCount);
        setData(newData);
        setOverviewTotal(total);
        setColors(newColors);
        setEntityTables({});
        setTablesToSampledRows({});
        setEntityToColumnIncidenceMap({});
        setOverviewTables([]);
        return;
      }
      setFolderIndex(null);

      // Install-wide entity-level config: drop HIDDEN entities before any transform so every
      // downstream view (pie, entity tables, column map) inherits the filtering. REPORT_ONLY
      // stays visible here — it's only excluded from export.
      const tables = filterTablesForDisplay(response?.tables ?? [], entityLevels);

      const tableSampledRowCountsMap = buildTableSampledRowCountsMapForEntityTables(tables);
      const tableColumnEntityMap = buildTableColumnEntityMapFromOverview(tables);
      const entityToColumnIncidenceMapValue = getEntityToColumnIncidenceMap(tableSampledRowCountsMap, tableColumnEntityMap);
      const overviewData = buildOverviewForPieVis(tables);
      const { otherCount, newData, total, newColors } = getPieVisData(overviewData);
      const entityTablesValue = buildEntityTablesFromOverview(tables);

      setEntityToColumnIncidenceMap(entityToColumnIncidenceMapValue);
      setTablesToSampledRows(tableSampledRowCountsMap);
      setOverview(overviewData);
      setOtherTotal(otherCount);
      setData(newData);
      setOverviewTotal(total);
      setColors(newColors);
      setEntityTables(entityTablesValue);
      setOverviewTables(tables);
    } catch (e) {
      console.log(e);
      setError(e?.message);
    }
  };

  // partialTick bumps when ReportPanel's slow partial-results poll lands a fresh snapshot
  // (5-minute cadence while the run is in progress). The refetch it triggers hits the
  // results cache that poll just warmed, so no extra payload transfer happens here.
  const partialTick = props.partialTick;
  useEffect(() => {
    const init = async (searchid) => {
      setLoading(true);
      await fetchDataHandler(searchid);
      setLoading(false);
    };
    if (searchid) init(searchid);
  }, [searchid, partialTick]);

  // Do NOT poll the full overview while the run is in progress. Each fetch re-downloads and
  // re-parses the whole 5–7 MB payload (56,518 fields at facets scale) and drives the worker
  // overview N+1 that starves the sampler pool (audit R6 / §3 UI). Instead, fetch exactly once
  // when the run transitions out of progress, so the final results land without the polling
  // storm. The App-level status poll keeps the progress bar live in the meantime.
  const wasInProgress = useRef(false);
  useEffect(() => {
    if (wasInProgress.current && !inProgress && searchid) {
      clearDiscoverResultsCache(searchid);
      fetchDataHandler(searchid);
    }
    wasInProgress.current = !!inProgress;
  }, [inProgress, searchid]);

  const candidateSources = scanMeta?.candidate_sources || [];

  const unprocessedItems = unprocessedExtensionsToItems(scanMeta?.unprocessed_extension_counts);
  const unprocessedTotal = unprocessedItems.reduce((n, it) => n + (Number(it.count) || 0), 0);
  // Separate strip, separate meaning: these files were removed by the source's exclusion
  // filter, not by a gap in what discovery can read.
  const excludedItems = excludedExtensionsToItems(scanMeta?.excluded_extension_counts);
  const excludedTotal = excludedItems.reduce((n, it) => n + (Number(it.count) || 0), 0);

  const hasArchiveSkips = (Number(scanMeta?.archive_skipped_count) || 0) > 0
    || (scanMeta?.unprocessed_extension_counts
      ? Object.keys(scanMeta.unprocessed_extension_counts).some((k) => LEGACY_ARCHIVE_SKIP_KEYS.has(k))
      : false);

  const sourceName = connectionDetails?.name || (typeof props.connection === 'string' ? props.connection : '');
  const sourceId = connectionDetails?.id ?? null;
  const sourceDsType = connectionDetails?.datasourceType || null;
  const sourceFileTypeLabel = sourceDsType ? (FILE_TYPE_LABELS[sourceDsType] || sourceDsType) : '—';
  const sourceRootPath = connectionDetails?.rootPath || connectionDetails?.config?.rootPath || '—';

  // Bundle the file-source detail rows for OverviewDonut to render inline (alongside
  // Engine for DB sources). Null for non-folder-search runs — donut keeps showing
  // Engine like before.
  const fileSourceDetails = isFolderSearch ? {
    dataSourceType: 'File',
    sourceName,
    sourceId,
    fileTypeLabel: sourceFileTypeLabel,
    rootPath: sourceRootPath,
  } : null;

  return (
    <FetchContainer
      fetchingData={loading}
      absolute={true}
    >
      <div data-testid="results-overview-tab">
        <div>
          <Row className="widget-row">
            <Col>
              <LevelCrumbs
                activeCrumb="overview"
                path={'results'}
                searchid={searchid}
                searchName={searchName}
                connection={props.connection}
                entity={donutLevel === 1 ? 'Low Incidence Findings' : null}
                resetFilter={() => setDonutLevel(0)}
              />
            </Col>
          </Row>

          <div>
            { props.currentSearch && overviewTotal === 0 ? (
              <Row>
                <Col className='text-center'>Analyzing data source..</Col>
              </Row>
            ) : (
              <Row className='mb-4' style={{ height:'430px' }}>
                <Col className='mh-100'>
                  <OverviewDonut
                    searchid={searchid}
                    searchName={searchName}
                    otherTotal={otherTotal}
                    donutLevel={donutLevel}
                    handleSetDonutLevel={(level) => setDonutLevel(level)}
                    dataLabel={props.dataLabel}
                    displayId={props.displayId}
                    searchDate={props.searchDate}
                    connection={props.connection}
                    connectionId={connectionDetails?.id ?? props.connectionId ?? null}
                    engine={props.engine}
                    data={data[donutLevel]}
                    overview={overview}
                    total={overviewTotal}
                    colors={colors}
                    targetType={props.targetType}
                    dbname={props.dbname}
                    sampleRules={sampleRules}
                    fileSourceDetails={fileSourceDetails}
                    runStats={props.runStats}
                    inProgress={inProgress}
                  />
                </Col>
                <Col className="text-left mh-100">
                  <OverviewTable
                    handleExportResults={() => props.handleExportResults()}
                    handleSetDonutLevel={(level) => setDonutLevel(level)}
                    overview={overview}
                    data={data[donutLevel]}
                    history={history}
                    searchid={searchid}
                    searchName={searchName}
                    total={overviewTotal}
                    colors={colors}
                    displayId={props.displayId}
                    engine={props.engine}
                    dataLabel={props.dataLabel}
                    donutLevel={donutLevel}
                  />
                </Col>
              </Row>
            )}
            {candidateSources.length > 0 && (
              <Row className='mt-3'>
                <Col>
                  {/* Candidate sources moved to their own sortable/searchable page — the
                      inline table topped out as an unscrollable wall once the worker
                      started surfacing below-sample-depth and document folders too. */}
                  <Card
                    className='shadow-sm widget-card text-left'
                    style={{ padding: 16, cursor: 'pointer' }}
                    onClick={() => history.push(`/results/${searchid}/candidate-sources`)}
                    data-testid='candidate-sources-summary'
                  >
                    <div className='d-flex align-items-center' style={{ gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>
                        Other data sources detected: {commafy(candidateSources.length)}
                      </span>
                      <span style={{ fontSize: 13, color: '#5C6473' }}>
                        Dedicated folders (and folders below the sampling depth) found inside this
                        scan — run a separate discovery to cover their contents.
                      </span>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        className='ml-auto'
                        onClick={(e) => {
                          e.stopPropagation();
                          history.push(`/results/${searchid}/candidate-sources`);
                        }}
                      >
                        View candidate sources →
                      </Button>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}

            {unprocessedItems.length > 0 && (
              <Row className='mt-3'>
                <Col>
                  <UnprocessedItemsPanel
                    title='Files not processed'
                    description={"File extensions in this folder that the scanner doesn't currently support. "
                      + 'These files were skipped — counts shown for transparency.'}
                    items={unprocessedItems}
                    collapsible
                    defaultCollapsed
                    collapsedTitle={`Files not processed: ${unprocessedTotal.toLocaleString()}`}
                    actionButton={hasArchiveSkips ? (
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        onClick={() => setSkippedDialogOpen(true)}
                      >
                        Full list
                      </Button>
                    ) : null}
                  />
                  <SkippedArchiveFilesDialog
                    show={skippedDialogOpen}
                    onHide={() => setSkippedDialogOpen(false)}
                    runId={searchid}
                    unprocessedItems={unprocessedItems}
                  />
                </Col>
              </Row>
            )}

            {excludedItems.length > 0 && (
              <Row className='mt-3'>
                <Col>
                  <UnprocessedItemsPanel
                    title='Excluded'
                    description={"Files this data source's exclusion filter kept out of the scan. "
                      + 'They were never opened or downloaded. Edit the exclusion filter on the '
                      + 'data source to include them.'}
                    items={excludedItems}
                    collapsible
                    defaultCollapsed
                    collapsedTitle={`Excluded: ${excludedTotal.toLocaleString()}`}
                    data-testid='excluded-items-panel'
                  />
                </Col>
              </Row>
            )}

            <Row className='mt-3'>
              <Col>
                {tableIndex ? (
                  <IndexResultsPanel
                    searchid={searchid}
                    dataLabel={props.dataLabel}
                    entity={entity}
                    totalTables={tableIndex.total_tables}
                    partialTick={partialTick}
                  />
                ) : folderIndex ? (
                  <FolderResultsPanel
                    searchid={searchid}
                    dataLabel={props.dataLabel}
                    folders={folderIndex}
                    entity={entity}
                    partialTick={partialTick}
                  />
                ) : (
                  <EntityMarkSeries
                    searchid={searchid}
                    dataLabel={props.dataLabel}
                    lowConfidence={donutLevel === 1}
                    overviewTables={overviewTables}
                    entityTables={donutLevel === 0 ? entityTables : entityTables['LOW_INCIDENCE_FINDINGS']}
                    entity={entity}
                    isEncrypted={isEncrypted}
                    encryptedTables={encryptedTables}
                    tablesToSampledRows={tablesToSampledRows}
                    entityToColumnIncidenceMap={entityToColumnIncidenceMap}
                  />
                )}
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </FetchContainer>
  );
}

export default OverviewPanel;